import React, { ChangeEvent, useCallback } from 'react';
import { InputMoney, InputPercent } from '~/components/NovosInputs';
import { useAlteracaoDePrecoPorItem } from '../../hooks/useAlteracaoDePrecoPorItem';
import { formatToCurrency } from '../../utils/formatToCurrency';
import { InputsContent } from './styles';
import {
  formatCurrencyAsText,
  moneyFormat,
  transformAsCurrency,
} from '~/utils/functions';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { calcMargemVenda, calcSugestaoVenda } from '../../utils/calculos';

export const ScreenFields: React.FC = () => {
  const {
    register,
    control,
    formState,
    setValue,
    getValues,
    onSave,
    disableContent,
    productSelected,
    handlesugestaoVendaPorMargem,
    sugestaoVendaPorMargem,
    handlesugestaoVendaPorCusto,
    sugestaoVendaPorCusto,
    precoArredondamento,
    warningLabel,
  } = useAlteracaoDePrecoPorItem();

  const recalcMargemCalculada = useCallback(
    (params: { custo: number; venda: number }) => {
      if (!productSelected) return 0;

      const calcMargVda = calcMargemVenda({
        product: productSelected,
        custoRep: params.custo,
        venda: params.venda,
      });

      setValue('val_margem_calculada', moneyFormat(String(calcMargVda)));
      return calcMargVda;
    },
    [productSelected, setValue],
  );

  const handleCustoInput = useCallback(
    async (ev: ChangeEvent<HTMLInputElement>) => {
      const { value } = ev.target;
      const val = formatToCurrency(value);

      if (!productSelected) return;

      const custo = transformAsCurrency(val);
      const venda = transformAsCurrency(getValues('val_venda'));

      const calcMargVda = recalcMargemCalculada({ custo, venda });

      if (calcMargVda) {
        const calcSugVda = await calcSugestaoVenda({
          product: productSelected,
          custoRep: custo,
          margem: productSelected.val_margem,
          precoArredondamento,
        });

        handlesugestaoVendaPorCusto(calcSugVda);
      }
    },
    [
      getValues,
      handlesugestaoVendaPorCusto,
      precoArredondamento,
      productSelected,
      recalcMargemCalculada,
    ],
  );

  const handleMargemInput = useCallback(
    async (ev: ChangeEvent<HTMLInputElement>) => {
      const { value } = ev.target;
      const val = formatToCurrency(value);

      if (productSelected) {
        const calcSugVda = await calcSugestaoVenda({
          product: productSelected,
          custoRep: transformAsCurrency(getValues('val_custo_rep')),
          margem: transformAsCurrency(val),
          precoArredondamento,
        });

        handlesugestaoVendaPorMargem(calcSugVda);
      }

      setValue('val_margem_calculada', val);
    },
    [
      getValues,
      handlesugestaoVendaPorMargem,
      precoArredondamento,
      productSelected,
      setValue,
    ],
  );

  const handleVendaInput = useCallback(
    async (ev: ChangeEvent<HTMLInputElement>) => {
      const { value } = ev.target;
      const val = formatToCurrency(value);

      if (!productSelected) return;

      const custo = transformAsCurrency(getValues('val_custo_rep'));
      const venda = transformAsCurrency(val);

      const calcMargVda = recalcMargemCalculada({ custo, venda });

      if (calcMargVda) {
        const calcSugVda = await calcSugestaoVenda({
          product: productSelected,
          custoRep: custo,
          margem: productSelected.val_margem,
          precoArredondamento,
        });

        handlesugestaoVendaPorCusto(calcSugVda);
      }
    },
    [
      getValues,
      handlesugestaoVendaPorCusto,
      precoArredondamento,
      productSelected,
      recalcMargemCalculada,
    ],
  );

  return (
    <InputsContent>
      <InputMoney
        label="Custo Reposição"
        min={0.0}
        max={99999}
        maxLength={9}
        placeholder="0,00"
        showIcon
        name="val_custo_rep"
        isError={!!formState.errors.val_custo_rep}
        register={register}
        disabled={disableContent}
        control={control}
        onInput={handleCustoInput}
      />
      <p
        style={{ color: 'red', marginTop: '3px' }}
        title="Calculado sobre o % de margem referência atual do produto, baseado no custo informado neste campo."
      >
        Sugestão por Custo: R$ {formatCurrencyAsText(sugestaoVendaPorCusto)}{' '}
        <IoMdInformationCircleOutline size={18} />
      </p>
      <InputPercent
        label="Margem Calculada"
        min={0.0}
        max={99999}
        maxLength={8}
        placeholder="0,00"
        showIcon
        name="val_margem_calculada"
        isError={!!formState.errors.val_margem_calculada}
        register={register}
        disabled={disableContent}
        control={control}
        onInput={handleMargemInput}
      />
      <p
        style={{ color: 'red', marginTop: '3px' }}
        title="Calculado sobre o % de margem informada neste campo, para que você faça simulações."
      >
        Sugestão por Margem: R$ {formatCurrencyAsText(sugestaoVendaPorMargem)}{' '}
        <IoMdInformationCircleOutline size={18} />
      </p>
      <InputMoney
        label="Venda"
        min={0.0}
        max={99999}
        maxLength={9}
        placeholder="0,00"
        showIcon
        name="val_venda"
        isError={!!formState.errors.val_venda}
        register={register}
        disabled={disableContent || warningLabel.type === 'composicao'}
        control={control}
        onKeyPress={async (event: any) => {
          if (event.key === 'Enter') {
            onSave();
          }
        }}
        onInput={handleVendaInput}
      />
    </InputsContent>
  );
};
